import React from "react";
import { BadgeCheck, ShieldQuestion } from "lucide-react";

/**
 * AdminApprovalBadge — small pill combo shown on admin queues, KYC pages
 * and startup/investor passports to reveal admin-side approval state.
 *
 * Rules:
 *   • "Approved by Admin"  → when `entity.kyc_status === "Approved"`. Rendered
 *     in navy so it doesn't compete with the gold VerifiedBadge.
 *   • "KYC Not Verified"   → adjacent warning pill when EITHER of:
 *        - `entity.admin_temp_approved === true` (admin bypass, no KYC yet)
 *        - trust-badge KYC (Aadhaar + CIN + GSTIN Sandbox) is incomplete
 *        - `entity.kyc_status !== "Approved"`
 *   • If entity has no admin approval at all → returns null.
 *
 * Accepts a startup OR investor doc; only shape assumed is a subset of
 * the fields the /admin/kyc-queue endpoint now exposes.
 */
export default function AdminApprovalBadge({ entity, size = "md", testId }) {
  const e = entity || {};
  const approvedByAdmin = e.kyc_status === "Approved";
  if (!approvedByAdmin) return null;

  // "KYC checks complete" is contextual — CIN/GSTIN only apply if the entity
  // actually carries one on record. Individuals (angels, HNIs) only need
  // identity verified via Sandbox OKYC.
  const needsCin = !!e.cin;
  const needsGstin = !!e.gstin;
  const kycChecksComplete =
    !!e.founder_id_verified &&
    (!needsCin || !!e.cin_verified_at) &&
    (!needsGstin || !!e.gstin_verified_at);
  const kycNotVerified = !!e.admin_temp_approved || !kycChecksComplete;

  const sz = size === "sm"
    ? { pill: "text-[10px] px-2 py-0.5 gap-1", icon: 10 }
    : { pill: "text-xs px-2.5 py-1 gap-1.5", icon: 12 };

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid={testId || "admin-approval-badge"}>
      <span
        className={`inline-flex items-center rounded-full font-heading font-semibold bg-navy text-cream border border-navy ${sz.pill}`}
        data-testid="admin-approval-badge-approved"
        title={
          e.admin_approved_by
            ? `Approved by ${e.admin_approved_by}${e.admin_approved_at ? ` on ${e.admin_approved_at.slice(0, 10)}` : ""}`
            : "Approved by VenturaZ Admin"
        }
      >
        <BadgeCheck size={sz.icon} />
        Approved by Admin
      </span>
      {kycNotVerified && (
        <span
          className={`inline-flex items-center rounded-full font-mono uppercase tracking-widest bg-amber-50 text-amber-900 border border-amber-300 ${sz.pill}`}
          data-testid="admin-approval-badge-kyc-not-verified"
          title={
            e.admin_temp_approved
              ? "Admin bypassed KYC — external verifications still pending"
              : "One or more Sandbox verifications (Aadhaar / CIN / GSTIN) still pending"
          }
        >
          <ShieldQuestion size={sz.icon} />
          KYC Not Verified
        </span>
      )}
    </span>
  );
}
